<!--
Generic link that automatically determines it's type based on the input
with optional click tracking.
-->
<template>
  <Component
    :is="type"
    v-bind="linkParams"
    ref="baseLink"
    class="base-link"
    @click="onClick">
    <slot/>
  </Component>
</template>

<script setup>
import { computed, useTemplateRef } from 'vue'
import useLink, { useClickTracker } from '@shared/composables/link.js'

const props = defineProps({
  to: {
    type: [String, Object],
    default: null
  },
  disabled: {
    type: Boolean,
    default: null
  },
  trackingName: {
    type: String,
    default: null,
    validator: (value, props) => !props.trackingData || value
  },
  trackingData: {
    type: Object,
    default: () => null
  }
})

const link = useLink(() => props.to)
const type = computed(() => props.disabled ? 'span' : link.type.value)
const linkParams = computed(() => props.disabled ? null : link.linkParams.value)

const emit = defineEmits({
  click: (event) => event instanceof MouseEvent
})

const onTrackedClick = useClickTracker(
  () => props.trackingName,
  'link',
  () => props.trackingData,
  emit,
  link
)
const baseLink = useTemplateRef('baseLink')
function onClick (event) {
  if (props.disabled) {
    event.stopPropagation()
    return
  }
  baseLink.value.blur?.() // Anchor
  baseLink.value.$el?.blur?.() // RouterLink
  if (props.trackingName) {
    onTrackedClick(event)
  } else {
    emit('click', event)
  }
}

defineExpose({ link })
</script>

<style scoped>
.base-link:visited {
  color: inherit;
}
</style>
